import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (error) {
    console.warn('[notifications] failed to obtain id token', (error as Error)?.message ?? error);
    return {};
  }
}

export interface NotificationRecord {
  id: string;
  clientId?: string;
  title: string;
  description?: string | null;
  type?: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
  createdAt: Date;
  status?: string;
  read: boolean;
  metadata?: Record<string, unknown>;
}

interface NotificationsContextValue {
  notifications: NotificationRecord[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

function mapPayload(rawNotifications: unknown): NotificationRecord[] {
  if (!Array.isArray(rawNotifications)) return [];
  return rawNotifications
    .map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
      const data = entry as Record<string, unknown>;
      const createdAtRaw = typeof data.createdAt === 'string' ? data.createdAt : '';
      const parsedCreatedAt = createdAtRaw ? new Date(createdAtRaw) : new Date();

      return {
        id: String(data.id || ''),
        clientId: typeof data.clientId === 'string' ? data.clientId : undefined,
        title: String(data.title || 'Notification'),
        description: typeof data.description === 'string' ? data.description : null,
        type: typeof data.type === 'string' ? data.type : undefined,
        severity: (typeof data.severity === 'string' ? data.severity : 'info') as NotificationRecord['severity'],
        createdAt: Number.isNaN(parsedCreatedAt.getTime()) ? new Date() : parsedCreatedAt,
        status: typeof data.status === 'string' ? data.status : undefined,
        read: data.read === true,
        metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata as Record<string, unknown> : undefined,
      } satisfies NotificationRecord;
    })
    .filter((item): item is NotificationRecord => !!item && !!item.id)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const authHeaders = await buildAuthHeaders();
        const response = await fetch('/api/notifications', {
          cache: 'no-store',
          headers: authHeaders,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(typeof payload?.error === 'string' ? payload.error : 'notifications_fetch_failed');
        }
        if (!cancelled) {
          setNotifications(mapPayload(payload.notifications));
        }
      } catch (error) {
        console.warn('[notifications] fetch failed', error);
        if (!cancelled) {
          setNotifications([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentUser?.uid, refreshToken]);

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!id) return;
      try {
        const authHeaders = await buildAuthHeaders();
        await fetch('/api/notifications/read', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({ notificationId: id }),
        });
        setNotifications((prev) => prev.map((notification) => (
          notification.id === id
            ? { ...notification, read: true }
            : notification
        )));
      } catch (error) {
        console.warn('[notifications] failed to mark as read', error);
      }
    },
    [],
  );

  const markAllAsRead = useCallback(
    async () => {
      const unread = notifications.filter(n => !n.read);
      if (unread.length === 0) return;

      try {
        const authHeaders = await buildAuthHeaders();
        await Promise.all(
          unread.map(n =>
            fetch('/api/notifications/read', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...authHeaders,
              },
              body: JSON.stringify({ notificationId: n.id }),
            })
          )
        );
        setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      } catch (error) {
        console.warn('[notifications] failed to mark all as read', error);
      }
    },
    [notifications],
  );

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      refresh,
    }),
    [notifications, unreadCount, loading, markAsRead, markAllAsRead, refresh],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return ctx;
}
